import type { QuerySpec } from '@helios/powersync-client-core';

// ─── Row Types ───

export interface HomePatientsServedRow {
  count: number;
}

export interface HomeQueueCountRow {
  total: number;
  waiting: number;
  called: number;
  serving: number;
}

export interface HomeCollectionsRow {
  total_cents: number;
  payment_count: number;
}

export interface HomeOutstandingRow {
  total_cents: number;
  invoice_count: number;
}

export interface HomeAlertRow {
  total: number;
  critical: number;
  warning: number;
}

export interface HomeQueueEntryRow {
  id: string;
  queue_number: number | null;
  patient_id: string;
  patient_name: string | null;
  service_name: string | null;
  provider_name: string | null;
  status: string;
  notes: string | null;
}

export interface HomeScheduleRow {
  id: string;
  patient_id: string | null;
  patient_name: string | null;
  service_name: string | null;
  start_time: string | null;
  duration_minutes: number | null;
  date: string | null;
  status: string | null;
}

export interface HomeRecentPatientRow {
  id: string;
  full_name: string | null;
  last_visited_at: string | null;
  photo_url: string | null;
  queue_status: string | null;
  encounter_status: string | null;
}

// ─── Queries ───

/**
 * Patients served today — count of distinct patients with encounters
 * created within [dayStartUtc, dayEndUtc), excluding cancelled/voided.
 */
export function qHomePatientsServedToday(
  dayStartUtc: string,
  dayEndUtc: string,
): QuerySpec<[string, string]> {
  return {
    key: ['home', 'patients-served', dayStartUtc] as const,
    sql: `SELECT COUNT(DISTINCT patient_id) AS count
      FROM patient_encounters
      WHERE (started_at >= ? AND started_at < ?)
        AND status NOT IN ('cancelled', 'voided')`,
    params: [dayStartUtc, dayEndUtc],
    watch: ['patient_encounters'],
  };
}

/**
 * Queue counts for today — total active, waiting, called, serving.
 */
export function qHomeQueueCounts(todayIso: string): QuerySpec<[string, string, string, string]> {
  return {
    key: ['home', 'queue-counts', todayIso] as const,
    sql: `SELECT
      COUNT(*) AS total,
      SUM(CASE WHEN status = 'waiting' THEN 1 ELSE 0 END) AS waiting,
      SUM(CASE WHEN status = 'called' THEN 1 ELSE 0 END) AS called,
      SUM(CASE WHEN status = 'serving' THEN 1 ELSE 0 END) AS serving
    FROM queue_entries
    WHERE queue_date = ? AND status IN ('waiting', 'called', 'serving', 'expected')`,
    params: [todayIso, todayIso, todayIso, todayIso],
    watch: ['queue_entries'],
  };
}

/**
 * Collections today — paid invoices where the payment happened today.
 * Uses invoice_payments.paid_at when available, falls back to invoices.updated_at.
 */
export function qHomeCollectionsToday(
  dayStartUtc: string,
  dayEndUtc: string,
): QuerySpec<[string, string]> {
  return {
    key: ['home', 'collections', dayStartUtc] as const,
    sql: `SELECT
      COALESCE(SUM(i.grand_total), 0) AS total_cents,
      COUNT(*) AS payment_count
    FROM invoices i
    WHERE i.status = 'paid'
      AND COALESCE(
        (SELECT ip.paid_at FROM invoice_payments ip WHERE ip.invoice_id = i.id ORDER BY ip.paid_at DESC LIMIT 1),
        i.updated_at
      ) >= ?
      AND COALESCE(
        (SELECT ip.paid_at FROM invoice_payments ip WHERE ip.invoice_id = i.id ORDER BY ip.paid_at DESC LIMIT 1),
        i.updated_at
      ) < ?`,
    params: [dayStartUtc, dayEndUtc],
    watch: ['invoices', 'invoice_payments'],
  };
}

/**
 * Outstanding invoices — unpaid total + count.
 */
export function qHomeOutstanding(): QuerySpec<[]> {
  return {
    key: ['home', 'outstanding'] as const,
    sql: `SELECT
      COALESCE(SUM(grand_total), 0) AS total_cents,
      COUNT(*) AS invoice_count
    FROM invoices
    WHERE status IN ('draft', 'issued')`,
    params: [],
    watch: ['invoices'],
  };
}

/**
 * Patient alerts — count by alert_level.
 */
export function qHomeAlerts(): QuerySpec<[]> {
  return {
    key: ['home', 'alerts'] as const,
    sql: `SELECT
      COUNT(*) AS total,
      SUM(CASE WHEN alert_level = 'critical' THEN 1 ELSE 0 END) AS critical,
      SUM(CASE WHEN alert_level = 'warning' THEN 1 ELSE 0 END) AS warning
    FROM patient_notes
    WHERE alert_level IN ('critical', 'warning', 'info')`,
    params: [],
    watch: ['patient_notes'],
  };
}

/**
 * Queue snapshot — entries for today grouped by status.
 */
export function qHomeQueueSnapshot(todayIso: string): QuerySpec<[string]> {
  return {
    key: ['home', 'queue-snapshot', todayIso] as const,
    sql: `SELECT q.id, q.queue_number, q.patient_id, p.full_name AS patient_name,
      s.name AS service_name, q.status, q.notes
    FROM queue_entries q
    LEFT JOIN patients p ON p.id = q.patient_id
    LEFT JOIN appointment_services s ON s.id = q.service_id
    WHERE q.queue_date = ? AND q.status IN ('waiting', 'called', 'serving')
    ORDER BY
      CASE q.status WHEN 'called' THEN 0 WHEN 'serving' THEN 1 WHEN 'waiting' THEN 2 ELSE 3 END,
      q.position ASC`,
    params: [todayIso],
    watch: ['queue_entries', 'patients', 'appointment_services'],
  };
}

/**
 * Today's schedule — appointments for today.
 */
export function qHomeScheduleToday(todayIso: string): QuerySpec<[string]> {
  return {
    key: ['home', 'schedule', todayIso] as const,
    sql: `SELECT a.id, a.patient_id, p.full_name AS patient_name,
      s.name AS service_name, a.start_time, a.duration_minutes, a.date, a.notes AS status
    FROM appointments a
    LEFT JOIN patients p ON p.id = a.patient_id
    LEFT JOIN appointment_services s ON s.id = a.service_id
    WHERE a.date = ?
    ORDER BY a.start_time ASC`,
    params: [todayIso],
    watch: ['appointments', 'patients', 'appointment_services'],
  };
}

export function qHomeScheduleRange(
  startDate: string,
  endDate: string,
): QuerySpec<[string, string]> {
  return {
    key: ['home', 'schedule-range', startDate, endDate] as const,
    sql: `SELECT a.id, a.patient_id, p.full_name AS patient_name,
      s.name AS service_name, a.start_time, a.duration_minutes, a.date, a.notes AS status
    FROM appointments a
    LEFT JOIN patients p ON p.id = a.patient_id
    LEFT JOIN appointment_services s ON s.id = a.service_id
    WHERE a.date >= ? AND a.date <= ?
    ORDER BY a.date ASC, a.start_time ASC`,
    params: [startDate, endDate],
    watch: ['appointments', 'patients', 'appointment_services'],
  };
}

/**
 * Recent patients — by last_visited_at or recent encounters.
 */
export function qHomeRecentPatients(): QuerySpec<[]> {
  return {
    key: ['home', 'recent-patients'] as const,
    sql: `SELECT p.id, p.full_name, p.last_visited_at, p.photo_url,
      (SELECT q.status FROM queue_entries q WHERE q.patient_id = p.id AND q.status IN ('waiting', 'called', 'serving') ORDER BY q.created_at DESC LIMIT 1) AS queue_status,
      (SELECT e.status FROM patient_encounters e WHERE e.patient_id = p.id ORDER BY e.created_at DESC LIMIT 1) AS encounter_status
    FROM patients p
    WHERE p.last_visited_at IS NOT NULL
    ORDER BY p.last_visited_at DESC
    LIMIT 5`,
    params: [],
    watch: ['patients', 'queue_entries', 'patient_encounters'],
  };
}

/**
 * Billing snapshot extras — oldest unpaid + top unpaid patient.
 */
export interface HomeBillingDetailRow {
  oldest_invoice_no: string | null;
  oldest_days: number | null;
  top_patient_name: string | null;
}

export function qHomeBillingDetails(): QuerySpec<[]> {
  return {
    key: ['home', 'billing-details'] as const,
    sql: `SELECT
      (SELECT invoice_no FROM invoices WHERE status IN ('draft', 'issued') ORDER BY created_at ASC LIMIT 1) AS oldest_invoice_no,
      (SELECT CAST(julianday('now') - julianday(created_at) AS INTEGER) FROM invoices WHERE status IN ('draft', 'issued') ORDER BY created_at ASC LIMIT 1) AS oldest_days,
      (SELECT p.full_name FROM invoices i JOIN patients p ON p.id = i.patient_id WHERE i.status IN ('draft', 'issued') GROUP BY i.patient_id ORDER BY SUM(i.grand_total) DESC LIMIT 1) AS top_patient_name`,
    params: [],
    watch: ['invoices', 'patients'],
  };
}

// ─── Drawer Detail Row Types ───

export interface HomePatientsServedDetailRow {
  encounter_id: string;
  patient_id: string;
  patient_name: string | null;
  started_at: string | null;
  status: string | null;
  provider_name: string | null;
  appointment_id: string | null;
}

export interface HomeQueueDetailRow {
  id: string;
  queue_number: number | null;
  patient_id: string;
  patient_name: string | null;
  service_name: string | null;
  provider_name: string | null;
  status: string;
  notes: string | null;
  arrived_at: string | null;
}

export interface HomeCollectionsDetailRow {
  invoice_id: string;
  patient_id: string;
  patient_name: string | null;
  invoice_no: string | null;
  grand_total: number;
  updated_at: string | null;
  payment_method: string | null;
  encounter_id: string | null;
}

export interface HomeOutstandingDetailRow {
  invoice_id: string;
  patient_id: string;
  patient_name: string | null;
  invoice_no: string | null;
  grand_total: number;
  created_at: string | null;
  days_overdue: number;
  encounter_id: string | null;
}

export interface HomeAlertsDetailRow {
  note_id: string;
  patient_id: string;
  patient_name: string | null;
  alert_level: string;
  title: string | null;
  body: string | null;
  created_at: string | null;
}

// ─── Drawer Detail Queries ───

/**
 * Patients served today — detail rows with encounter info.
 * appointment_id is non-null when the encounter came from an appointment (vs walk-in).
 */
export function qHomePatientsServedDetail(
  dayStartUtc: string,
  dayEndUtc: string,
): QuerySpec<[string, string]> {
  return {
    key: ['home', 'patients-served-detail', dayStartUtc] as const,
    sql: `SELECT e.id AS encounter_id, e.patient_id, p.full_name AS patient_name,
      e.started_at, e.status, e.appointment_id,
      COALESCE(ap.display_name, ap.full_name) AS provider_name
    FROM patient_encounters e
    LEFT JOIN patients p ON p.id = e.patient_id
    LEFT JOIN appointment_providers ap ON ap.id = e.provider_id
    WHERE (e.started_at >= ? AND e.started_at < ?)
      AND e.status NOT IN ('cancelled', 'voided')
    ORDER BY e.started_at DESC`,
    params: [dayStartUtc, dayEndUtc],
    watch: ['patient_encounters', 'patients', 'appointment_providers'],
  };
}

/**
 * Queue detail — entries for today with provider name.
 */
export function qHomeQueueDetail(todayIso: string): QuerySpec<[string]> {
  return {
    key: ['home', 'queue-detail', todayIso] as const,
    sql: `SELECT q.id, q.queue_number, q.patient_id, p.full_name AS patient_name,
      s.name AS service_name,
      COALESCE(ap.display_name, ap.full_name) AS provider_name,
      q.status, q.notes, q.arrived_at
    FROM queue_entries q
    LEFT JOIN patients p ON p.id = q.patient_id
    LEFT JOIN appointment_services s ON s.id = q.service_id
    LEFT JOIN appointment_providers ap ON ap.id = q.provider_id
    WHERE q.queue_date = ? AND q.status IN ('waiting', 'called', 'serving')
    ORDER BY
      CASE q.status WHEN 'serving' THEN 0 WHEN 'called' THEN 1 WHEN 'waiting' THEN 2 ELSE 3 END,
      q.position ASC`,
    params: [todayIso],
    watch: ['queue_entries', 'patients', 'appointment_services', 'appointment_providers'],
  };
}

/**
 * Collections today detail — paid invoices with payment method.
 */
export function qHomeCollectionsDetail(
  dayStartUtc: string,
  dayEndUtc: string,
): QuerySpec<[string, string]> {
  return {
    key: ['home', 'collections-detail', dayStartUtc] as const,
    sql: `SELECT i.id AS invoice_id, i.patient_id, p.full_name AS patient_name,
      i.invoice_no, i.grand_total, i.encounter_id,
      COALESCE(
        (SELECT ip.paid_at FROM invoice_payments ip WHERE ip.invoice_id = i.id ORDER BY ip.paid_at DESC LIMIT 1),
        i.updated_at
      ) AS updated_at,
      (SELECT ip.method FROM invoice_payments ip WHERE ip.invoice_id = i.id ORDER BY ip.paid_at DESC LIMIT 1) AS payment_method
    FROM invoices i
    LEFT JOIN patients p ON p.id = i.patient_id
    WHERE i.status = 'paid'
      AND COALESCE(
        (SELECT ip.paid_at FROM invoice_payments ip WHERE ip.invoice_id = i.id ORDER BY ip.paid_at DESC LIMIT 1),
        i.updated_at
      ) >= ?
      AND COALESCE(
        (SELECT ip.paid_at FROM invoice_payments ip WHERE ip.invoice_id = i.id ORDER BY ip.paid_at DESC LIMIT 1),
        i.updated_at
      ) < ?
    ORDER BY updated_at DESC`,
    params: [dayStartUtc, dayEndUtc],
    watch: ['invoices', 'patients', 'invoice_payments'],
  };
}

/**
 * Outstanding detail — unpaid invoices with overdue days.
 */
export function qHomeOutstandingDetail(): QuerySpec<[]> {
  return {
    key: ['home', 'outstanding-detail'] as const,
    sql: `SELECT i.id AS invoice_id, i.patient_id, p.full_name AS patient_name,
      i.invoice_no, i.grand_total, i.created_at, i.encounter_id,
      CAST(julianday('now') - julianday(i.created_at) AS INTEGER) AS days_overdue
    FROM invoices i
    LEFT JOIN patients p ON p.id = i.patient_id
    WHERE i.status IN ('draft', 'issued')
    ORDER BY days_overdue DESC`,
    params: [],
    watch: ['invoices', 'patients'],
  };
}

/**
 * Patient alerts detail — notes with alert level, sorted by severity.
 */
export function qHomeAlertsDetail(): QuerySpec<[]> {
  return {
    key: ['home', 'alerts-detail'] as const,
    sql: `SELECT n.id AS note_id, n.patient_id, p.full_name AS patient_name,
      n.alert_level, n.title, n.body, n.created_at
    FROM patient_notes n
    LEFT JOIN patients p ON p.id = n.patient_id
    WHERE n.alert_level IN ('critical', 'warning', 'info')
    ORDER BY
      CASE n.alert_level WHEN 'critical' THEN 0 WHEN 'warning' THEN 1 ELSE 2 END,
      n.created_at DESC`,
    params: [],
    watch: ['patient_notes', 'patients'],
  };
}
